import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { CreateSiteRequest } from '../../services/api';
import { validate, collectErrors } from '../../utils/validation';

interface AddSiteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (site: CreateSiteRequest) => Promise<void>;
    isCreating: boolean;
}

const AddSiteModal: React.FC<AddSiteModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isCreating
}) => {
    const emptyForm: CreateSiteRequest = { name: '', code: '', addressLine1: '', addressLine2: '', city: '', postcode: '' };

    const [newSite, setNewSite] = useState<CreateSiteRequest>(emptyForm);
    const [errors, setErrors] = useState<Partial<Record<keyof CreateSiteRequest, string>>>({});

    const validateForm = (): boolean => {
        const result = collectErrors({
            name: validate.required(newSite.name, 'Site name'),
            code: validate.required(newSite.code, 'Site code') ?? validate.siteCode(newSite.code),
            addressLine1: validate.required(newSite.addressLine1, 'Address line 1'),
            city: validate.required(newSite.city, 'City'),
            postcode: validate.required(newSite.postcode, 'Postcode') ?? validate.ukPostcode(newSite.postcode ?? ''),
        });
        setErrors(result);
        return Object.keys(result).length === 0;
    };

    const handleChange = (field: keyof CreateSiteRequest, value: string) => {
        setNewSite(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        await onSubmit(newSite);
        setNewSite(emptyForm);
        setErrors({});
    };

    const handleClose = () => {
        setNewSite(emptyForm);
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Add New Site</h3>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    {(() => {
                        const rs = { '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties;
                        const ic = (f: keyof CreateSiteRequest) =>
                            `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors[f] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;
                        return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
                            <input type="text" placeholder="e.g., London Depot" className={ic('name')} style={rs}
                                value={newSite.name} onChange={(e) => handleChange('name', e.target.value)} />
                            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Site Code *</label>
                            <input type="text" placeholder="e.g., LON-01" className={`${ic('code')} font-mono`} style={rs}
                                value={newSite.code} onChange={(e) => handleChange('code', e.target.value.toUpperCase())} />
                            {errors.code
                                ? <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                                : <p className="mt-1 text-xs text-gray-500">Unique identifier (letters, numbers, hyphens)</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
                            <input type="text" placeholder="Street address" className={ic('addressLine1')} style={rs}
                                value={newSite.addressLine1} onChange={(e) => handleChange('addressLine1', e.target.value)} />
                            {errors.addressLine1 && <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                            <input type="text" placeholder="Building, suite, etc. (optional)"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2" style={rs}
                                value={newSite.addressLine2} onChange={(e) => handleChange('addressLine2', e.target.value)} />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                                <input type="text" placeholder="London" className={ic('city')} style={rs}
                                    value={newSite.city} onChange={(e) => handleChange('city', e.target.value)} />
                                {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode *</label>
                                <input type="text" placeholder="SW1A 1AA" className={ic('postcode')} style={rs}
                                    value={newSite.postcode} onChange={(e) => handleChange('postcode', e.target.value.toUpperCase())} />
                                {errors.postcode && <p className="mt-1 text-sm text-red-600">{errors.postcode}</p>}
                            </div>
                        </div>
                    </div>
                        );
                    })()}

                    {/* Footer */}
                    <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating}
                            className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center"
                            style={{
                                backgroundColor: '#69BE28',
                                '--tw-ring-color': 'rgba(105, 190, 40, 0.5)'
                            } as React.CSSProperties}
                        >
                            {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {isCreating ? 'Creating...' : 'Create Site'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddSiteModal;
